import React, { useState, useEffect } from 'react';
import { X, Copy, CheckCircle2, FileText, FileCode, Database, AlertCircle } from 'lucide-react';
import { AZURE } from '../../services/api';

const ArtifactViewer = ({ isOpen, onClose, artifactId, fallbackContent, title }) => {
  const [artifact, setArtifact] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (artifactId && !fallbackContent) {
        fetchArtifact(artifactId);
      } else {
        // Use fallback content (e.g. prompt string passed directly)
        setArtifact({
          content: fallbackContent,
          title: title || 'Artifact Content',
          type: 'text',
          language: 'markdown'
        });
      }
    } else {
      setArtifact(null); // Reset on close
    }
  }, [isOpen, artifactId, fallbackContent]);

  const fetchArtifact = async (id) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await AZURE.get(`/governance/artifacts/${id}`);
      if (data.success) {
        setArtifact(data.data);
      } else {
        setError("Failed to load artifact");
      }
    } catch (err) {
      setError("Error loading artifact: " + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (artifact?.content) {
      navigator.clipboard.writeText(artifact.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">
        
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gray-50">
          <div className="flex items-center gap-3">
             <div className="p-2 bg-blue-100 text-blue-700 rounded-lg">
               {artifact?.type === 'sql_script' ? <Database className="w-5 h-5"/> : 
                artifact?.type === 'code' ? <FileCode className="w-5 h-5"/> : 
                <FileText className="w-5 h-5"/>}
             </div>
             <div>
               <h3 className="font-bold text-gray-900">{artifact?.title || title || 'Artifact Viewer'}</h3>
               <div className="flex gap-2 text-xs text-gray-500">
                 {artifactId && <span className="font-mono">{artifactId}</span>}
                 {artifact?.type && <span className="uppercase border px-1 rounded bg-white">{artifact.type}</span>}
               </div>
             </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-0 bg-[#1e1e1e]">
          {loading ? (
             <div className="flex flex-col items-center justify-center h-64 text-gray-400">
               <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mb-2"></div>
               Loading artifact...
             </div>
          ) : error ? (
             <div className="flex flex-col items-center justify-center h-64 text-red-400 p-8 text-center">
               <AlertCircle className="w-12 h-12 mb-2 opacity-50"/>
               {error}
             </div>
          ) : (
             <pre className="p-6 text-sm font-mono text-gray-300 whitespace-pre-wrap leading-relaxed">
               {artifact?.content || ''}
             </pre>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100 bg-white flex justify-end gap-2">
          <button 
            onClick={handleCopy}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-semibold text-gray-700"
          >
            {copied ? <CheckCircle2 className="w-4 h-4 text-green-600"/> : <Copy className="w-4 h-4"/>}
            {copied ? 'Copied!' : 'Copy Content'}
          </button>
          <button 
            onClick={onClose}
            className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-semibold"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ArtifactViewer;
